import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

import HttpManager from "../../../../services/HttpManager";
import { ArticleDTO } from "../../../../models/ArticleDTO";
import PaginationComponent from "../../../../components/pagination/pagination";
import { buildPages } from "../../managment/enrollmentRequests/helpers";
import { FilterOptions, KitFilters, apiError, bestEnrollment } from "../kits/shared";
import FiltrosTienda, { FILTRO_VACIO, FiltroTienda, aKitFilters, fechasDe } from "./filtrosTienda";
import { Compro, PagoChip, Producto, Sub, monto, vendidoPor } from "./piezasTienda";
import DetalleVenta from "./detalleVenta";

/*
	Ventas › Ventas (Módulos Claros, 2026-11-01): las ventas de productos del plantel. Reemplaza Reporte de Ventas, Abonos sin
	Liquidar y Anulaciones (ahora son la pastilla Pago). Las 4 cifras de arriba solo para quien tiene Control de Ventas (38).
	Fecha de la venta = su primer pago (lo que filtra y lo que se ve son lo mismo); sin pago o anulada = cuando se cargó.
*/

const PAGE_SIZES = [100, 250];
const BASE: FiltroTienda = { ...FILTRO_VACIO, preset: "mes" };

/** Pago → pestaña del backend y campo de fecha. */
const consulta = (pago: string): { tab: string; statusIds?: string[]; fecha: "paid" | "charged"; sort: string } => {
	switch (pago) {
		case "pagado": return { tab: "sold", statusIds: ["2", "3"], fecha: "paid", sort: "firstPaid" };
		case "abono": return { tab: "sold", statusIds: ["1"], fecha: "paid", sort: "firstPaid" };
		case "sinPago": return { tab: "pending_unpaid", fecha: "charged", sort: "charged" };
		case "anulado": return { tab: "cancelled", fecha: "charged", sort: "charged" };
		default: return { tab: "sold", fecha: "paid", sort: "firstPaid" };
	}
};

export interface TabVentasProps {
	campusId: number;
	sym: string;
	movil: boolean;
	q: string;
	section: "kitSales" | "newSale";
	opciones: FilterOptions | null;
	conCifras: boolean;
	canAct: boolean;
	/** Sube cuando se cargó una venta nueva (la lista se vuelve a pedir). */
	vuelta: number;
	onAviso: (texto: string) => void;
}

const TabVentas: React.FC<TabVentasProps> = ({ campusId, sym, movil, q, section, opciones, conCifras, canAct, vuelta, onAviso }) => {
	const http = HttpManager.getInstance().kitsService;
	const [filtro, setFiltro] = useState<FiltroTienda>(BASE);
	const [page, setPage] = useState<number>(1);
	const [perPage, setPerPage] = useState<number>(PAGE_SIZES[0]);
	const [rows, setRows] = useState<ArticleDTO[] | null>(null);
	const [total, setTotal] = useState<number>(0);
	const [lastPage, setLastPage] = useState<number>(1);
	const [cifras, setCifras] = useState<any | null>(null);
	const [loading, setLoading] = useState<boolean>(false);
	const [error, setError] = useState<string | null>(null);
	const [abierta, setAbierta] = useState<ArticleDTO | null>(null);
	const [propia, setPropia] = useState<number>(0);
	const requestRef = useRef<number>(0);

	const c = consulta(filtro.pago);
	const filtros = useCallback((): KitFilters => ({
		...aKitFilters(filtro, c.fecha), q, sort: c.sort, dir: "desc",
		...(c.statusIds ? { statusIds: c.statusIds } : {})
	}), [filtro, q, c.fecha, c.sort, c.statusIds && c.statusIds.join(",")]);

	useEffect(() => { setPage(1); }, [q, campusId]);

	useEffect(() => {
		if (!campusId) return;
		const request = ++requestRef.current;
		setLoading(true);
		setError(null);
		const f = filtros();
		const lista = http.list(campusId, section, c.tab, f, page, perPage);
		// «Inscritas con Kit» con el mismo rango que la lista (si no hay rango, el backend toma 30 días).
		const { from, to } = fechasDe(filtro);
		// Las cifras siguen la misma pastilla Pago y el mismo campo de fecha que la lista (revisión A4).
		const sumas = conCifras ? http.salesTotals(campusId, "kitSales", { ...aKitFilters(filtro, c.fecha), q, enrolledFrom: from, enrolledTo: to, ...(c.statusIds ? { statusIds: c.statusIds } : {}), ...(c.tab !== "sold" ? { tab: c.tab } : {}) }) : Promise.resolve(null);
		Promise.all([lista, sumas]).then(([l, s]) => {
			if (request !== requestRef.current) return;
			setRows(l.data.data || []);
			setTotal(l.data.total || 0);
			setLastPage(l.data.last_page || 1);
			setCifras(s ? s.data : null);
		}).catch((e) => {
			if (request !== requestRef.current) return;
			setError(apiError(e, "No se pudo cargar la información."));
			setRows([]);
		}).finally(() => { if (request === requestRef.current) setLoading(false); });
	}, [campusId, section, c.tab, filtros, page, perPage, conCifras, vuelta, propia]);

	const pages = useMemo(() => buildPages(page, lastPage, (n) => { if (n >= 1 && n <= lastPage) setPage(n); }, movil), [page, lastPage, movil]);
	const desde = (page - 1) * perPage + 1;
	const enPagina = rows ? rows.length : 0;
	const itemCount = { start: enPagina ? desde : 0, end: enPagina ? desde + enPagina - 1 : 0, total };
	const fecha = (k: ArticleDTO) => (c.fecha === "paid" ? k.firstPaidAt : null) || k.createdAt;
	const tenue = { opacity: loading ? 0.6 : 1 };
	const pct = (v: number | null | undefined) => v === null || v === undefined ? "-" : v.toLocaleString("es-MX", { maximumFractionDigits: 1 }) + " %";

	const lista = () => {
		if (!rows) return <div className="loader" />;
		if (rows.length < 1) return <div className="wvtSub" style={{ textAlign: "center", padding: 24, fontSize: 15 }}>Sin ventas con estos filtros.</div>;
		if (movil) return <div style={{ display: "flex", flexDirection: "column", gap: 8, paddingBottom: 8 }}>
			{rows.map((k) => <div key={k.id} className="wvtTarjeta" style={tenue} role="button" tabIndex={0} onClick={() => setAbierta(k)}>
				<div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}><Compro kit={k} enrollment={bestEnrollment(k)} /><div style={{ flexShrink: 0 }}><PagoChip kit={k} /></div></div>
				<div style={{ fontSize: 14, marginTop: 4 }}><Producto kit={k} /> · <b>{monto(sym, k.subTotal?.value)}</b></div>
				<Sub>{fecha(k)} · {vendidoPor(k)}</Sub>
			</div>)}
		</div>;
		return <table className="table table-hover wvtLista">
			<thead><tr><th>Alumna</th><th>Producto</th><th style={{ width: 170 }}>Vendido Por</th><th className="wvtNum" style={{ width: 120 }}>Total</th><th style={{ width: 110 }}>Pago</th><th style={{ width: 100 }}>{c.fecha === "paid" ? "Vendido" : "Cargado"}</th></tr></thead>
			<tbody>{rows.map((k) => <tr key={k.id} className="wvtFila" style={tenue} onClick={() => setAbierta(k)}>
				<td><Compro kit={k} enrollment={bestEnrollment(k)} /></td>
				<td><Producto kit={k} /></td>
				<td>{vendidoPor(k)}</td>
				<td className="wvtNum">{monto(sym, k.subTotal?.value)}{k.isDebt ? <Sub><span className="wvtOro">Falta {monto(sym, k.pendingAmount?.value)}</span></Sub> : null}</td>
				<td><PagoChip kit={k} /></td>
				<td style={{ whiteSpace: "nowrap" }}>{fecha(k)}</td>
			</tr>)}</tbody>
		</table>;
	};

	return <>
		<FiltrosTienda valor={filtro} onChange={(v) => { setFiltro(v); setPage(1); }} opciones={opciones} base={BASE}
			etiquetaFecha={c.fecha === "paid" ? "Vendidas" : "Cargadas"} pago producto vendidoPor mas />
		{conCifras && cifras ? <div className="wvtKpis">
			<div><small>Kits Completos</small><b>{cifras.kitsComplete}</b></div>
			<div><small>Vitrina</small><b>{cifras.vitrina}</b></div>
			<div><small>Cobrado</small><b>{monto(sym, cifras.paid)}</b></div>
			<div title={cifras.enrolledWithKit + " de " + cifras.enrolled}><small>Inscritas con Kit</small><b>{pct(cifras.enrolledWithKitPct)}</b></div>
		</div> : null}
		{error ? <div className="alert alert-danger" style={{ marginTop: 12, fontSize: 15 }}>{error}</div> : null}
		<div className="waScrollX" style={{ overflowX: "auto", marginTop: 14 }}>{lista()}</div>
		<div className="waSobreDock" style={{ marginTop: 10 }}>
			<PaginationComponent pages={pages} pageSizes={PAGE_SIZES} itemCount={itemCount} onPageSizeChange={(n) => { setPerPage(n); setPage(1); }} />
		</div>
		{abierta ? <DetalleVenta campusId={campusId} sym={sym} kit={abierta} section={section} canAct={canAct} onClose={() => setAbierta(null)}
			onChanged={(texto) => { setAbierta(null); onAviso(texto); setPropia((n) => n + 1); }} /> : null}
	</>;
};

export default TabVentas;
